import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Brain, MessageCircle, ChevronRight, RefreshCw,
  AlertTriangle, CheckCircle, ArrowUpRight, Send, Shield,
  Activity, Target, Loader2, ChevronDown, ChevronUp,
  Upload, FileText, Users, Scale, Play, Mail, Bell, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500", ring: "stroke-red-500", icon: AlertTriangle },
  high: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500", ring: "stroke-amber-500", icon: Zap },
  medium: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-400", ring: "stroke-blue-400", icon: Target },
  low: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600", dot: "bg-gray-400", ring: "stroke-gray-400", icon: CheckCircle },
};

const AGENT_LABELS = {
  borrower_intake_agent: "Intake",
  underwriting_triage_agent: "Underwriting",
  deal_packaging_agent: "Packaging",
  lender_match_agent: "Lender Match",
  closing_coordinator_agent: "Closing",
  servicing_monitoring_agent: "Servicing",
  investor_memo_agent: "Investor Memo",
  ops_exception_agent: "Exceptions",
};

// Map action_types to deep tab keys and navigation
const ACTION_ROUTES = {
  upload_doc: { tab: "documents_full", label: "Upload Documents" },
  complete_fields: { tab: "edit", label: "Edit Deal" },
  trigger_underwriting: { tab: "underwriting", label: "View Underwriting" },
  trigger_packaging: { tab: "deal_package", label: "View Deal Package" },
  trigger_lender_match: { tab: "matching", label: "View Lender Matches" },
  review_matches: { tab: "matching", label: "Review Lender Matches" },
  select_lender: { tab: "matching", label: "Select Lender" },
  review_risks: { tab: "underwriting", label: "Review Risk Factors" },
  assign_reviewer: { tab: "underwriting", label: "Underwriting Review" },
  request_info: { tab: "requests", label: "Manage Requests" },
  submit_term_sheet: { tab: "matching", label: "Submit Term Sheet" },
  resolve_blocker: { tab: "exceptions", label: "Resolve Blockers" },
  resolve_exception: { tab: "exceptions", label: "Resolve Exception" },
  review_package: { tab: "deal_package", label: "Review Package" },
  advance_stage: { tab: null, label: "Advance Stage" },
  request_docs: { tab: "requests", label: "Request Documents" },
  follow_up_ts: { tab: "matching", label: "Follow Up on Term Sheet" },
  contact_borrower: { tab: null, label: "Contact Borrower" },
  review_anomalies: { tab: "servicing_full", label: "Review Servicing" },
  send_reminder: { tab: null, label: "Send Reminder" },
  view_deal: { tab: null, label: "View Deal" },
  review_memo: { tab: null, label: "Review Memo" },
  payment_action: { tab: "servicing_full", label: "Servicing" },
  manual_review: { tab: "underwriting", label: "Manual Review" },
};

// Non-tab action types that need backend execution
const NON_TAB_ACTIONS = new Set(["contact_borrower", "send_reminder", "advance_stage", "review_memo"]);

function ConfidenceRing({ score, size = 28 }) {
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#059669" : score >= 60 ? "#d97706" : "#dc2626";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f0ece4" strokeWidth="2.5" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="2.5"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <span className="absolute text-[7px] font-black" style={{ color }}>{score}</span>
    </div>
  );
}

export default function CopilotPanel({ leadId, onOpenTab, onNavigate }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsg, setChatMsg] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [agentsExpanded, setAgentsExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [agentsRunning, setAgentsRunning] = useState(false);
  const [executingAction, setExecutingAction] = useState(null);
  const [actionHistory, setActionHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const chatEndRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchIntel = useCallback(() => {
    if (!leadId) return;
    axios.get(`${API}/copilot/v2/deal-intel?lead_id=${leadId}`, { headers })
      .then(r => {
        setIntel(r.data);
        // If agents are running, check again in 3s
        const hasRunning = Object.values(r.data.agents || {}).some(a => a.status === "running");
        setAgentsRunning(hasRunning);
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [leadId]);

  useEffect(() => { fetchIntel(); }, [fetchIntel]);

  // Fetch action history
  useEffect(() => {
    if (!leadId) return;
    axios.get(`${API}/copilot/v2/action-history?lead_id=${leadId}&limit=10`, { headers })
      .then(r => setActionHistory(r.data?.actions || []))
      .catch(() => {});
  }, [leadId]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchIntel, 30000);
    return () => clearInterval(interval);
  }, [fetchIntel]);

  // Quick-poll when agents are running
  useEffect(() => {
    if (!agentsRunning) return;
    const interval = setInterval(fetchIntel, 3000);
    return () => clearInterval(interval);
  }, [agentsRunning, fetchIntel]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchIntel();
  };

  const handleActionClick = (action) => {
    const route = ACTION_ROUTES[action.action_type];
    // Tab-based navigation
    if (route?.tab && onOpenTab) {
      onOpenTab(route.tab);
      return;
    }
    // View deal navigation
    if (action.action_type === "view_deal" && action.lead_id && onNavigate) {
      onNavigate("deal", action.lead_id);
      return;
    }
    // Non-tab actions that require backend execution
    if (NON_TAB_ACTIONS.has(action.action_type)) {
      executeAction(action);
    }
  };

  const executeAction = async (action) => {
    const actionLabel = ACTION_ROUTES[action.action_type]?.label || action.action_type;
    const needsConfirm = action.action_type === "advance_stage";
    if (needsConfirm && !window.confirm(`Are you sure you want to "${actionLabel}" for this deal?`)) return;

    setExecutingAction(action.action_type);
    try {
      const r = await axios.post(`${API}/copilot/v2/execute-action`, {
        action_type: action.action_type,
        lead_id: leadId,
        label: action.label || actionLabel,
      }, { headers });
      const data = r.data;
      if (data.status === "completed") {
        toast.success(data.message || `${actionLabel} completed`);
        if (data.action_type === "review_memo" && data.opportunity_id && onNavigate) {
          onNavigate("opportunity", data.opportunity_id);
        }
        // Refresh intel after action
        setTimeout(() => {
          fetchIntel();
          // Also refresh action history
          axios.get(`${API}/copilot/v2/action-history?lead_id=${leadId}&limit=10`, { headers })
            .then(r => setActionHistory(r.data?.actions || []))
            .catch(() => {});
        }, 500);
      } else if (data.status === "info") {
        toast.info(data.message);
      } else {
        toast.error(data.message || "Action failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to execute action");
    } finally {
      setExecutingAction(null);
    }
  };

  const sendChat = async () => {
    if (!chatMsg.trim()) return;
    const msg = chatMsg.trim();
    setChatMsg("");
    setChatHistory(prev => [...prev, { role: "user", text: msg }]);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/copilot/v2/chat`, { message: msg, lead_id: leadId }, { headers });
      setChatHistory(prev => [...prev, { role: "ai", text: r.data.response, status: r.data.status }]);
    } catch {
      setChatHistory(prev => [...prev, { role: "ai", text: "Unable to get response. Try again.", status: "error" }]);
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EDE8DC] p-6 flex items-center justify-center min-h-[200px]" data-testid="copilot-loading">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!intel) return null;

  const { next_actions = [], insights = [], agents = {} } = intel;
  const agentEntries = Object.entries(agents);
  const hasAgents = agentEntries.length > 0;
  const actionCount = next_actions.length;

  return (
    <div className="space-y-4" data-testid="copilot-panel">
      {/* Header with action count badge */}
      <div className="flex items-center justify-between" data-testid="copilot-header">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shadow-md ${agentsRunning ? "animate-pulse" : ""}`}>
            <Brain className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Copilot Intelligence</h3>
              {actionCount > 0 && (
                <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center" data-testid="copilot-action-count">
                  {actionCount}
                </span>
              )}
            </div>
            <p className="text-[9px] text-[#999] uppercase tracking-wider">
              {agentsRunning ? (
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Agents running...
                </span>
              ) : (
                `${intel.total_agents_run} agent${intel.total_agents_run !== 1 ? "s" : ""} active`
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="h-7 w-7 rounded-lg bg-[#FDFCF9] border border-[#EDE8DC] flex items-center justify-center text-[#999] hover:text-emerald-600 hover:border-emerald-200 transition-all"
          data-testid="copilot-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* 1. NEXT ACTIONS (PRIMARY — CLICKABLE) */}
      {actionCount > 0 && (
        <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-4" data-testid="copilot-next-actions">
          <div className="flex items-center gap-1.5 mb-3">
            <Zap className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-700 font-bold">Next Actions</span>
            <span className="text-[9px] text-emerald-500 ml-auto">{actionCount} action{actionCount !== 1 ? "s" : ""}</span>
          </div>
          <div className="space-y-2">
            {next_actions.map((action, i) => {
              const style = PRIORITY_STYLES[action.priority] || PRIORITY_STYLES.medium;
              const Icon = style.icon;
              const route = ACTION_ROUTES[action.action_type];
              const isNonTab = NON_TAB_ACTIONS.has(action.action_type);
              const isClickable = !!route || isNonTab;
              const isExecuting = executingAction === action.action_type;
              const ActionIcon = action.action_type === "contact_borrower" ? Mail
                : action.action_type === "send_reminder" ? Bell
                : action.action_type === "advance_stage" ? Play
                : null;
              return (
                <button
                  key={i}
                  onClick={() => isClickable && !isExecuting && handleActionClick(action)}
                  disabled={!isClickable || isExecuting}
                  className={`flex items-center gap-3 p-2.5 rounded-lg ${style.bg} border ${style.border} transition-all w-full text-left group ${isClickable && !isExecuting ? "hover:shadow-md hover:scale-[1.01] cursor-pointer" : "opacity-90"} ${isExecuting ? "animate-pulse" : ""}`}
                  data-testid={`copilot-action-${i}`}
                >
                  <div className={`h-6 w-6 rounded-lg flex items-center justify-center ${style.bg} border ${style.border} shrink-0`}>
                    {isExecuting ? (
                      <Loader2 className={`h-3 w-3 ${style.text} animate-spin`} />
                    ) : (
                      <Icon className={`h-3 w-3 ${style.text}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${style.text} truncate`}>{action.label}</p>
                    {action.source && (
                      <p className="text-[9px] text-[#999] mt-0.5">via {AGENT_LABELS[action.source + "_agent"] || action.source}</p>
                    )}
                  </div>
                  {isClickable && !isExecuting && (
                    isNonTab && ActionIcon ? (
                      <ActionIcon className={`h-3.5 w-3.5 ${style.text} opacity-50 group-hover:opacity-100 transition-all shrink-0`} />
                    ) : (
                      <ArrowUpRight className={`h-3.5 w-3.5 ${style.text} opacity-0 group-hover:opacity-100 transition-all shrink-0`} />
                    )
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. AI INSIGHTS */}
      {insights.length > 0 && (
        <div className="rounded-xl bg-white border border-[#EDE8DC] p-4" data-testid="copilot-insights">
          <div className="flex items-center gap-1.5 mb-3">
            <Activity className="h-3.5 w-3.5 text-[#E5C98B]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#888] font-bold">AI Insights</span>
          </div>
          <div className="space-y-2">
            {insights.map((insight, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`copilot-insight-${i}`}>
                <div className="h-1.5 w-1.5 rounded-full bg-[#E5C98B] mt-1.5 shrink-0" />
                <p className="text-xs text-[#555] leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. AGENT STATUS with confidence rings */}
      {hasAgents && (
        <div className="rounded-xl bg-white border border-[#EDE8DC] p-4" data-testid="copilot-agents">
          <button
            onClick={() => setAgentsExpanded(!agentsExpanded)}
            className="flex items-center justify-between w-full"
            data-testid="copilot-agents-toggle"
          >
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#888] font-bold">Agent Status</span>
              <span className="text-[9px] text-[#bbb] ml-1">{agentEntries.length} agents</span>
            </div>
            {agentsExpanded ? <ChevronUp className="h-3.5 w-3.5 text-[#bbb]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#bbb]" />}
          </button>
          {agentsExpanded && (
            <div className="mt-3 space-y-2">
              {agentEntries.map(([name, data]) => {
                const statusColor = data.status === "completed" ? "bg-emerald-500" : data.status === "needs_review" ? "bg-amber-500" : data.status === "running" ? "bg-blue-500 animate-pulse" : "bg-gray-400";
                return (
                  <div key={name} className="flex items-center gap-2.5 p-2 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]" data-testid={`agent-status-${name}`}>
                    <div className={`h-2 w-2 rounded-full ${statusColor} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-[#555]">{AGENT_LABELS[name] || name}</span>
                      </div>
                      {data.summary && <p className="text-[9px] text-[#999] mt-0.5 truncate">{data.summary}</p>}
                    </div>
                    {data.confidence_score > 0 && <ConfidenceRing score={data.confidence_score} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 4. ACTION HISTORY */}
      {actionHistory.length > 0 && (
        <div className="rounded-xl bg-white border border-[#EDE8DC] p-4" data-testid="copilot-action-history">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="flex items-center justify-between w-full"
            data-testid="copilot-history-toggle"
          >
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[#999]" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#888] font-bold">Action History</span>
              <span className="text-[9px] text-[#bbb] ml-1">{actionHistory.length}</span>
            </div>
            {historyExpanded ? <ChevronUp className="h-3.5 w-3.5 text-[#bbb]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#bbb]" />}
          </button>
          {historyExpanded && (
            <div className="mt-3 space-y-2">
              {actionHistory.map((action, i) => {
                const typeIcon = action.action_type === "contact_borrower" ? Mail
                  : action.action_type === "send_reminder" ? Bell
                  : action.action_type === "advance_stage" ? Play
                  : CheckCircle;
                const TypeIcon = typeIcon;
                const timeStr = action.created_at ? new Date(action.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                return (
                  <div key={action.event_id || i} className="flex items-start gap-2.5 p-2 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]" data-testid={`action-history-${i}`}>
                    <div className="h-5 w-5 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                      <TypeIcon className="h-2.5 w-2.5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold text-[#555] truncate">{action.summary}</p>
                      <p className="text-[9px] text-[#bbb] mt-0.5">{timeStr}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 5. ASK COPILOT (CHAT) */}
      <div className="rounded-xl bg-white border border-[#EDE8DC] overflow-hidden" data-testid="copilot-chat-section">
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className="flex items-center justify-between w-full p-4 hover:bg-[#FDFCF9] transition-colors"
          data-testid="copilot-chat-toggle"
        >
          <div className="flex items-center gap-1.5">
            <MessageCircle className="h-3.5 w-3.5 text-[#999]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#888] font-bold">Ask Copilot</span>
          </div>
          {chatOpen ? <ChevronUp className="h-3.5 w-3.5 text-[#bbb]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#bbb]" />}
        </button>
        {chatOpen && (
          <div className="border-t border-[#f0ece4] p-4">
            {chatHistory.length > 0 && (
              <div className="max-h-48 overflow-y-auto mb-3 space-y-2" data-testid="copilot-chat-history">
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                      msg.role === "user"
                        ? "bg-emerald-600 text-white"
                        : msg.status === "error"
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-[#FDFCF9] text-[#333] border border-[#EDE8DC]"
                    }`} data-testid={`chat-msg-${i}`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-[#FDFCF9] border border-[#EDE8DC] rounded-xl px-3 py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
            {chatHistory.length === 0 && (
              <div className="mb-3 space-y-1.5">
                {["What's the risk on this deal?", "Summarize the lender matches", "What should I do next?"].map((q, i) => (
                  <button key={i} onClick={() => { setChatMsg(q); }} className="block w-full text-left text-[10px] text-[#888] hover:text-emerald-600 px-2 py-1 rounded hover:bg-emerald-50 transition-colors" data-testid={`chat-suggestion-${i}`}>
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                value={chatMsg}
                onChange={e => setChatMsg(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendChat()}
                placeholder="Ask about this deal..."
                className="flex-1 text-xs bg-[#FDFCF9] border border-[#EDE8DC] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 placeholder:text-[#ccc]"
                data-testid="copilot-chat-input"
              />
              <button
                onClick={sendChat}
                disabled={chatLoading || !chatMsg.trim()}
                className="h-8 w-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                data-testid="copilot-chat-send"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
